/* eslint-disable max-len */
// src/components/contentHub/TrendsIntelligence.js
// Quick Picks mode: reads from cache only, shows top trends with best angle.
// Links to standalone /trends page for full discovery.

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { FiTrendingUp, FiZap, FiArrowRight } from 'react-icons/fi';

const CATEGORY_CONFIG = {
    breaking: { label: 'Breaking', className: 'bg-red-400/15 text-red-300 border-red-400/25' },
    rising: { label: 'Rising', className: 'bg-amber-400/15 text-amber-300 border-amber-400/25' },
    seasonal: { label: 'Seasonal', className: 'bg-green-400/15 text-green-300 border-green-400/25' },
    evergreen: { label: 'Evergreen', className: 'bg-slate-blue/15 text-slate-blue border-slate-blue/25' },
};

function QuickPickCard({ trend, onSelectAngle }) {
    const category = CATEGORY_CONFIG[trend.category] || CATEGORY_CONFIG.rising;
    const bestAngle = trend.contentAngles?.[0];
    const angleText = typeof bestAngle === 'object' ? bestAngle.angle : bestAngle;

    return (
        <div className="p-4 bg-warm-gray/20 border border-white/5 rounded-2xl hover:border-slate-blue/30 transition-all">
            <div className="flex items-start justify-between gap-2 mb-1.5">
                <h4 className="text-sm font-bold text-cream leading-snug flex-1">{trend.title}</h4>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${category.className}`}>
                    {category.label}
                </span>
            </div>
            {angleText && (
                <button
                    onClick={() => onSelectAngle(angleText, trend.title)}
                    className="w-full text-left p-2.5 mt-2 bg-black/20 border border-white/5 rounded-xl text-xs text-cream/70 hover:text-cream hover:border-slate-blue/40 hover:bg-slate-blue/10 transition-all flex items-center gap-2 group"
                >
                    <FiZap className="h-3 w-3 text-cream/20 group-hover:text-slate-blue shrink-0 transition-colors" />
                    <span className="flex-1 line-clamp-2">{angleText}</span>
                </button>
            )}
        </div>
    );
}

QuickPickCard.propTypes = {
    trend: PropTypes.object.isRequired,
    onSelectAngle: PropTypes.func.isRequired,
};

function TrendsIntelligence({ projectId, onSelectTrend, disabled, platform }) {
    const navigate = useNavigate();
    const [trends, setTrends] = useState([]);
    const [loaded, setLoaded] = useState(false);

    // Determine which platform cache to read
    const cachePlatform = (platform === 'linkedin') ? 'linkedin' : 'meta';

    // Read directly from Firestore cache (no Gemini call)
    useEffect(() => {
        if (!projectId) return;
        let cancelled = false;

        const readCache = async () => {
            try {
                const cacheRef = doc(db, 'projects', projectId, 'trendCache', cachePlatform);
                const snap = await getDoc(cacheRef);
                if (cancelled) return;
                if (snap.exists()) {
                    const data = snap.data();
                    setTrends((data.trends || []).slice(0, 4));
                }
            } catch {
                // Silently fail — quick picks are non-critical
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };

        readCache();
        return () => { cancelled = true; };
    }, [projectId, cachePlatform]);

    const handleSelectAngle = useCallback((angle, trendTitle) => {
        onSelectTrend(`${trendTitle}: ${angle}`);
    }, [onSelectTrend]);

    // Empty / no cache state
    if (loaded && trends.length === 0) {
        return (
            <div className="text-center p-8 space-y-3">
                <FiTrendingUp className="h-8 w-8 text-cream/20 mx-auto" />
                <p className="text-sm text-cream/40">No cached trends yet.</p>
                <button
                    onClick={() => navigate(`/trends?project=${projectId}`)}
                    className="text-xs font-bold text-slate-blue hover:underline inline-flex items-center gap-1"
                >
                    Discover trends for this project <FiArrowRight className="h-3 w-3" />
                </button>
            </div>
        );
    }

    // Loading (very brief since it's a local Firestore read)
    if (!loaded) {
        return (
            <div className="space-y-3 animate-pulse">
                {[1, 2].map((i) => (
                    <div key={i} className="p-4 bg-warm-gray/20 border border-white/5 rounded-2xl space-y-2">
                        <div className="h-4 w-3/4 bg-white/10 rounded" />
                        <div className="h-3 w-full bg-white/5 rounded" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
                <label className="form-label flex items-center gap-2">
                    <FiTrendingUp className="h-4 w-4 text-slate-blue" />
                    Quick picks from your niche
                </label>
            </div>

            <p className="text-xs text-cream/30 -mt-2">
                Click a content idea to use it as your spark
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {trends.map((trend, index) => (
                    <QuickPickCard
                        key={index}
                        trend={trend}
                        onSelectAngle={handleSelectAngle}
                    />
                ))}
            </div>

            <div className="text-center pt-2">
                <button
                    onClick={() => navigate(`/trends?project=${projectId}`)}
                    className="text-xs font-bold text-slate-blue hover:underline inline-flex items-center gap-1"
                >
                    Explore all trends <FiArrowRight className="h-3 w-3" />
                </button>
            </div>
        </div>
    );
}

TrendsIntelligence.propTypes = {
    projectId: PropTypes.string.isRequired,
    onSelectTrend: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    platform: PropTypes.string,
};

export default TrendsIntelligence;
